import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NearestPoiService } from '../nearest-poi.service';
import { MapBoxService } from '../../../../../mapbox/mapbox.service';
import { MatTooltip } from '@angular/material/tooltip';
import { pointsMinZoom } from '../../../../../../shared/util/consts';
import { ORI_LAYER } from '../../../../../mapbox/services/layer-store.service';
import { schoolsPoiLayers } from '../../education/education.service';
import { BreakpointObserverService } from '../../../../../../shared/services/breakpoint-observer.service';
import { LayersMenuService } from '../../layers-menu.service';

@Component({
  selector: 'app-nearest-poi-panel',
  standalone: true,
  imports: [CommonModule, MatTooltip],
  templateUrl: './poi-distance-block.component.html',
  styleUrls: [
    './poi-distance-block.component.scss',
    '../../../../../../app.component.scss',
  ],
  providers: [NearestPoiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NearestPoiPanelComponent implements OnInit, OnDestroy {
  public nearestPoiService = inject(NearestPoiService);
  public mapboxService = inject(MapBoxService);
  private breakpointObserver = inject(BreakpointObserverService);
  private menuService = inject(LayersMenuService);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  @Input() isSchool = false;
  @Input() isOri = false; // police

  public label!: string;
  public hasVisiblePois = false;
  private layerIds: string[] = [];

  ngOnInit() {
    this.nearestPoiService.setMode(this.isSchool ? 'school' : 'ori');

    if (this.isOri) {
      this.label = 'nearest police station';
    } else if (this.isSchool) {
      this.label = 'nearest school';
    }

    this.layerIds = this.isSchool ? schoolsPoiLayers : [ORI_LAYER];

    // Trigger initial fetch
    this.onMoveEnd();
    // Subscribe to map moves for this panel instance
    this.mapboxService.map.on('moveend', this.onMoveEnd);
    // Check visible pois on idle too to update when poi loaded
    this.mapboxService.map.on('idle', this.checkVisiblePois);
  }

  ngOnDestroy() {
    this.mapboxService.map.off('moveend', this.onMoveEnd);
    this.mapboxService.map.off('idle', this.checkVisiblePois);
  }

  /**
   * The map is built outside the Angular zone, so 'moveend' and 'idle' arrive
   * outside it too. Both handlers below end up in the template - directly via
   * hasVisiblePois, and through poi$, whose debounceTime and HTTP call would
   * otherwise both be scheduled outside the zone and mark this OnPush view
   * dirty with no tick left to render it.
   */
  private inZone(fn: () => void): void {
    this.zone.run(() => {
      fn();
      this.cdr.markForCheck();
    });
  }

  private onMoveEnd = () =>
    this.inZone(() => {
      const c = this.mapboxService.map.getCenter();
      this.nearestPoiService.setCenter(c);
      this.updateVisiblePois();
    });

  private checkVisiblePois = () => this.inZone(() => this.updateVisiblePois());

  private updateVisiblePois(): void {
    const map = this.mapboxService.map;

    if (!map) {
      this.hasVisiblePois = false;
      return;
    }

    if (map.getZoom() < pointsMinZoom) {
      this.hasVisiblePois = false;
      return;
    }

    const layers = this.layerIds;

    if (layers.length === 0) {
      this.hasVisiblePois = false;
      return;
    }

    const canvas = map.getCanvas();
    const isMobile = this.breakpointObserver.isMobile.value;

    const featuress = map.queryRenderedFeatures(
      // Add threshold to compensate right menu
      [
        [0, 0],
        [canvas.width - (isMobile ? 0 : 500), canvas.height],
      ],
      { layers }
    );
    this.hasVisiblePois = featuress.length > 0;
  }

  public handleGoTo(lat: number, lng: number) {
    if (this.breakpointObserver.isMobile.value) {
      this.menuService.isMenuOpened = false;
    }

    const curZoom = this.mapboxService.map.getZoom();
    // Outside the zone: the map is built outside it, but a camera animation
    // started from in here inherits *this* zone for its whole flight, and
    // GL JS drives that flight with requestAnimationFrame - a patched one,
    // so every frame of it would run application-wide change detection.
    this.zone.runOutsideAngular(() =>
      this.mapboxService.map.flyTo({
        center: [lng, lat],
        zoom: curZoom >= pointsMinZoom ? curZoom : pointsMinZoom,
      })
    );
  }
}
